import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SchoolApi } from '../../../api/client';
import type { AlertRuleSettingsRow } from '../../../api/types';
import { Badge, Button, Chip, Switch } from '../../../components/ui';
import { TrendingUp, Clock, HeartPulse, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import { categoryOf, type RuleCategory } from '../../../lib/alertRuleMeta';
import { prettifySlug } from '../../../lib/caseStatus';
import ThresholdInput from './ThresholdInput';
import { nameOr } from '../../../lib/names';

export interface RuleDraft { isEnabled: boolean; overrides: Record<string, unknown> }

export const ruleDraftDirty = (rule: AlertRuleSettingsRow, d: RuleDraft): boolean =>
  d.isEnabled !== rule.is_enabled || JSON.stringify(d.overrides) !== JSON.stringify(rule.threshold_overrides);

interface Props {
  rule: AlertRuleSettingsRow;
  /** Page-controlled mode: the page owns the draft and saves all changed rules from one bar. */
  draft?: RuleDraft;
  onDraftChange?: (code: string, next: RuleDraft) => void;
}

const CATEGORY_ICON: Record<RuleCategory, typeof TrendingUp> = {
  academic: TrendingUp,
  engagement: Clock,
  wellbeing: HeartPulse,
  integrity: ShieldCheck,
  other: SlidersHorizontal,
};

const SEVERITY_TONE: Record<string, 'info' | 'warning' | 'danger'> = {
  info: 'info', warning: 'warning', critical: 'danger',
};

function formatSavedDate(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(iso));
}

export default function AlertRuleCard({ rule, draft: controlled, onDraftChange }: Props) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<RuleDraft>({ isEnabled: rule.is_enabled, overrides: rule.threshold_overrides });
  const draft = controlled ?? local;
  const { isEnabled, overrides } = draft;
  const pageControlled = controlled != null && onDraftChange != null;
  const update = (fn: (d: RuleDraft) => RuleDraft) => {
    if (pageControlled) onDraftChange!(rule.code, fn(draft));
    else setLocal(fn);
  };
  const setIsEnabled = (v: boolean) => update((d) => ({ ...d, isEnabled: v }));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isDirty = useMemo(() => ruleDraftDirty(rule, { isEnabled, overrides }), [isEnabled, overrides, rule]);

  const onThresholdChange = (key: string) => (next: unknown | undefined) => {
    update((d) => {
      const out = { ...d.overrides };
      if (next === undefined) delete out[key];
      else out[key] = next;
      return { ...d, overrides: out };
    });
  };

  const onSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await SchoolApi.alertRuleOverride(rule.code, {
        is_enabled: isEnabled,
        threshold_overrides: overrides,
      });
      qc.invalidateQueries({ queryKey: ['school', 'alert-rules'] });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: unknown } } | null)?.response?.data
          ? JSON.stringify((e as { response: { data: unknown } }).response.data)
          : 'Save failed';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const defaultKeys = Object.keys(rule.default_threshold_config);
  const Icon = CATEGORY_ICON[categoryOf(rule.code)];

  return (
    <section
      aria-label={rule.name}
      className={`flex min-w-0 flex-col gap-3.5 rounded-[20px] border bg-surface p-[22px] shadow-card ${isDirty ? 'border-accent ring-1 ring-accent' : 'border-line'}`}
    >
      <div className="flex items-start gap-3.5">
        <span className={`flex h-[42px] w-[42px] flex-none items-center justify-center rounded-[13px] ${isEnabled ? 'bg-mist text-slate' : 'bg-neu-bg text-control'}`}>
          <Icon size={18} strokeWidth={1.8} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[17px] font-bold tracking-[-0.01em] text-ink">{rule.name}</h3>
          <p className="mt-[3px] break-all font-mono text-[11px] font-semibold text-muted">{rule.code}</p>
        </div>
        <Switch tone="navy" label={`${isEnabled ? 'Turn off' : 'Turn on'} ${rule.name}`} checked={isEnabled} onChange={setIsEnabled} />
      </div>

      <div className={`flex flex-1 flex-col gap-3.5 transition-opacity ${isEnabled ? '' : 'opacity-50'}`}>
        <p className="text-[13px] text-muted">{rule.description}</p>

        <div className="flex flex-wrap gap-1.5">
          <Badge tone={SEVERITY_TONE[rule.default_severity] ?? 'neutral'}>{prettifySlug(rule.default_severity)}</Badge>
          <span className="inline-flex items-center whitespace-nowrap rounded-full border border-line bg-panel px-2.5 py-1 text-xs font-semibold text-muted-2">
            Notifies {prettifySlug(rule.default_audience).toLowerCase()}
          </span>
        </div>

        {defaultKeys.length > 0 && (
          <div className="grid grid-cols-1 gap-2.5 rounded-[14px] bg-panel p-3.5 min-[641px]:grid-cols-2">
            {defaultKeys.map((key) => (
              <ThresholdInput
                key={key}
                name={key}
                idPrefix={rule.code}
                defaultValue={rule.default_threshold_config[key]}
                overrideValue={overrides[key]}
                onChange={onThresholdChange(key)}
              />
            ))}
          </div>
        )}
      </div>

      {saveError && (
        <div role="alert" className="rounded-[12px] bg-danger-bg p-2.5 text-sm text-danger-ink">
          {saveError}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-3 text-xs">
        <div className="min-w-0 text-muted">
          {rule.updated_at
            ? `Last saved by ${nameOr(rule.updated_by_name, rule.updated_by_username)} · ${formatSavedDate(rule.updated_at)}`
            : 'Using school defaults'}
        </div>
        <div className="flex items-center gap-2">
          {isDirty ? <Badge tone="warning">Unsaved</Badge> : <Chip tone={isEnabled ? 'up' : 'info'}>{isEnabled ? 'On' : 'Off'}</Chip>}
          {!pageControlled && (
            <Button size="sm" onClick={onSave} disabled={!isDirty || saving} loading={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
